/** @jsxImportSource preact */
import * as React from "preact";
import { ComponentChildren, FunctionComponent } from "preact";
import { useState } from "preact/hooks";

import cx from "classnames";

import SVGIcon from "../../../app/components/SVGIcon";
import SavedItemsButton, { ISavedItemsButton } from "../SavedItemsButton";
import FaqFeedbackCta from "../FaqFeedback/FaqFeedbackCta";

export interface IConcertina {
  className?: string;
  title: string;
  description?: string;
  additionalText?: string | null;
  id: string;
  isRichtext: boolean;
  children?: ComponentChildren;
  isOpenByDefault?: boolean;
  isCompact?: boolean;
  isRelatedDocuments?: boolean;
  savedItemsButton?: ISavedItemsButton;
  renderFaqFeedback?: boolean;
}

const Concertina: FunctionComponent<IConcertina> = ({
  className,
  title,
  description,
  additionalText,
  id,
  children,
  isRichtext,
  isRelatedDocuments = false,
  isOpenByDefault = false,
  isCompact = false,
  savedItemsButton,
  renderFaqFeedback,
}) => {
  // Default to the accessible "no-js" fallback unless we can confirm on the client that JS is enabled.
  const hasNoJsClass =
    typeof document === "undefined"
      ? true
      : document.body?.classList.contains("no-js") ||
        document.documentElement.classList.contains("no-js") ||
        (className?.split(/\s+/).includes("no-js") ?? false);
  const [isOpen, setIsOpen] = useState<boolean>(isOpenByDefault || hasNoJsClass);
  const isExpanded = hasNoJsClass || isOpen;

  const open = () => {
    setIsOpen(!isOpen);
  };

  const titleContent = (
    <span className="concertina__title">
      {isRelatedDocuments && (
        <SVGIcon
          name="document-related"
          className="concertina__icon--document-related"
        />
      )}
      {title}
    </span>
  );

  return (
    <div className={cx("concertina__item accordion-item", className)}>
      <div
        className="concertina__header accordion-head"
      >
        {hasNoJsClass ? (
          <div
            className={cx("concertina__trigger", {
              "concertina__trigger--compact": isCompact,
            })}
          >
            {titleContent}
          </div>
        ) : (
          <button
            className={cx("concertina__trigger concertina__trigger--button unbutton", {
              "concertina__trigger--compact": isCompact,
            })}
            aria-controls={`.concertina__body-wrapper--${id}`}
            onClick={open}
            role="tab"
            type="button"
            aria-selected={isExpanded}
            aria-expanded={isExpanded}
          >
            {titleContent}
            <SVGIcon
              name="cross"
              className="concertina__icon concertina__icon--closed"
            />
            <SVGIcon
              name="dash"
              className="concertina__icon concertina__icon--open"
            />
          </button>
        )}
      </div>
      <div
        className={cx(
          "concertina__body-wrapper accordion-body",
          id && `concertina__body-wrapper--${id}`,
          {
            expand: isExpanded,
            collapse: !isExpanded,
          },
        )}
        aria-hidden={hasNoJsClass ? undefined : !isExpanded}
        hidden={hasNoJsClass ? undefined : !isExpanded}
      >
        <div
          className={cx("concertina__body", {
            "richtext-content": isRichtext,
          })}
        >
          <div>
            {description && <div dangerouslySetInnerHTML={{ __html: description }} />}
            {additionalText && <div dangerouslySetInnerHTML={{ __html: additionalText }} />}
            {children}
            {renderFaqFeedback && <FaqFeedbackCta />}
          </div>
          {savedItemsButton && <SavedItemsButton {...savedItemsButton} />}
        </div>
      </div>
    </div>
  );
};

export default Concertina;
